import BigNumber from 'bignumber.js';
import _ from 'lodash';
import { SaveIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { StrategyResponse, StrategyWeightResponse } from '../../../response/responseSchema';
import modalStore from '@client/stores/modalStore';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';
import Jsonld from '../Jsonld';
import MetaHeader from '@client/component/MetaHeader';
import AIRecommendModalContent from './AIRecommendModalContent';
import { StrategyInfoEditBox } from './components/StrategyInfoSection';
import StrategyStockActions from './components/StrategyStockActions';
import { StockSearchResult } from './StockSearchInput';
import StrategyWeightInput, { WeightItem } from './StrategyWeightInput';

class ViewStore {
    // API 응답 데이터
    strategy: StrategyResponse | null = null;
    latestWeight: StrategyWeightResponse | null = null;

    strategyHash = '';
    strategyName = '';
    description = '';
    startDate = '';
    weights: WeightItem[] = [];
    isLoading = true;
    isSubmitting = false;

    // 초기값 저장 (변경 여부 비교용)
    private initialStrategyName = '';
    private initialDescription = '';
    private initialWeights: WeightItem[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(hash: string) {
        this.isLoading = true;
        this.strategyHash = hash;

        try {
            // 1. 전략 기본 정보 조회
            const strategy = await apiClient.strategy.public.getStrategy.$forceQuery({ hash });

            // 2. 최신 비중 조회
            const latestWeight = await apiClient.strategy.public.getLatestWeight.$forceQuery({
                hash,
            });

            runInAction(() => {
                this.strategy = strategy;
                this.latestWeight = latestWeight;

                this.strategyName = strategy.name;
                this.description = strategy.description || '';
                this.startDate = strategy.startDate;

                // weightsJson을 WeightItem 배열로 변환
                const weights = latestWeight?.weightsJson
                    ? Object.entries(latestWeight.weightsJson).map(([ticker, weight]) => ({
                          ticker,
                          name: ticker, // TODO: 종목명 조회 필요
                          weight: weight as number,
                      }))
                    : [];

                // 비중이 높은 순으로 정렬
                this.weights = weights.sort((a, b) => b.weight - a.weight);

                // 초기값 저장 (변경 여부 비교용)
                this.initialStrategyName = this.strategyName;
                this.initialDescription = this.description;
                this.initialWeights = _.cloneDeep(this.weights);

                this.isLoading = false;
            });
        } catch (error) {
            console.error('Failed to fetch strategy:', error);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    setStrategyName(name: string) {
        this.strategyName = name;
    }

    setDescription(desc: string) {
        this.description = desc;
    }

    addStock(stock: StockSearchResult) {
        // 이미 추가된 종목인지 확인
        const exists = this.weights.some((w) => w.ticker === stock.ticker);
        if (exists) {
            return;
        }

        // 새 종목 추가 (기본 비중 10%)
        runInAction(() => {
            this.weights.push({
                ticker: stock.ticker,
                name: stock.nameKo || stock.name,
                weight: 0.1,
            });
        });
    }

    addStocks(stocks: StockSearchResult[]) {
        stocks.forEach((stock) => this.addStock(stock));
    }

    updateWeight(ticker: string, weight: number) {
        // immutable 방식으로 배열 업데이트 (MobX가 변경을 감지할 수 있도록)
        this.weights = this.weights.map((item) =>
            item.ticker === ticker ? { ...item, weight } : item,
        );
    }

    removeStock(ticker: string) {
        this.weights = this.weights.filter((w) => w.ticker !== ticker);
    }

    get totalWeight() {
        // BigNumber로 정확한 합계 계산
        return this.weights
            .reduce((sum, item) => sum.plus(item.weight), new BigNumber(0))
            .toNumber();
    }

    get isDirty() {
        // 전략명 변경 확인
        if (this.strategyName !== this.initialStrategyName) {
            return true;
        }
        // 설명 변경 확인
        if (this.description !== this.initialDescription) {
            return true;
        }
        // 종목 비중 변경 확인 (깊은 비교)
        if (!_.isEqual(this.weights, this.initialWeights)) {
            return true;
        }
        return false;
    }

    get canSubmit() {
        return (
            this.isDirty &&
            this.strategyName.trim().length > 0 &&
            this.weights.length > 0 &&
            this.totalWeight > 0 &&
            this.totalWeight <= 1
        );
    }

    get disabledReason() {
        if (!this.isDirty) {
            return '변경사항이 없습니다';
        }
        if (this.strategyName.trim().length === 0) {
            return '전략명을 입력해주세요';
        }
        if (this.weights.length === 0) {
            return '최소 1개 이상의 종목을 추가해주세요';
        }
        if (this.totalWeight <= 0) {
            return '총 비중이 0%입니다';
        }
        if (this.totalWeight > 1) {
            return '총 비중을 100% 이하로 맞춰주세요';
        }
        return null;
    }

    async submit() {
        if (!this.canSubmit) {
            return false;
        }

        this.isSubmitting = true;

        try {
            // weightsJson 형태로 변환
            const weightsJson = Object.fromEntries(this.weights.map((w) => [w.ticker, w.weight]));

            // 전략 업데이트 API 호출
            await apiClient.strategy.member.update.mutate({
                hash: this.strategyHash,
                name: this.strategyName,
                description: this.description,
                weights: weightsJson,
            });

            // 캐시 갱신
            // TODO 확인 필요
            apiClient.strategy.public.getStrategy.$invalidate({ hash: this.strategyHash });
            apiClient.strategy.public.getLatestWeight.$invalidate({ hash: this.strategyHash });

            return true;
        } catch (error) {
            console.error('전략 수정 실패:', error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
}

const StrategyEditPage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigateBack } = useCommonNavigate();
    const { hash } = useParams<{ hash: string }>();

    useEffect(() => {
        if (hash) {
            viewStore.fetch(hash);
        }
    }, [viewStore, hash]);

    const handleAIRecommend = async () => {
        const { returnValue } = await modalStore.custom<StockSearchResult[]>({
            title: 'AI 종목 추천',
            renderComponent: ({ modalId }) => (
                <AIRecommendModalContent
                    modalId={modalId}
                    excludeTickers={viewStore.weights.map((w) => w.ticker)}
                />
            ),
            showCloseButton: true,
            preventClose: false,
        });

        if (returnValue && returnValue.length > 0) {
            viewStore.addStocks(returnValue);
        }
    };

    const handleSubmit = async () => {
        const success = await viewStore.submit();
        if (success) {
            navigateBack();
        }
    };

    if (viewStore.isLoading) {
        return (
            <Frame>
                <Box isLoading={true} />
                <Box isLoading={true} />
                <Box isLoading={true} />
            </Frame>
        );
    }

    const title = '전략 수정';
    const description = `${viewStore.strategyName} 전략을 수정합니다.`;

    return (
        <Frame>
            <MetaHeader title={title} description={description} />
            <Jsonld type="Blog" title={title} description={description} />

            {/* 헤더 */}
            <Frame.Title title={title} />

            {/* 종목 구성 (최우선) */}
            <Box>
                <Box.Title title="종목 구성" />

                {/* 비중 입력 */}
                <StrategyWeightInput
                    weights={viewStore.weights}
                    onWeightChange={(ticker, weight) => viewStore.updateWeight(ticker, weight)}
                    onRemove={(ticker) => viewStore.removeStock(ticker)}
                />

                {/* AI 추가 버튼 + 검색 입력 */}
                <StrategyStockActions
                    weights={viewStore.weights}
                    onAIRecommend={handleAIRecommend}
                    onStockSelect={(stock) => viewStore.addStock(stock)}
                />
            </Box>

            {/* 전략 정보 (접을 수 있음) */}
            <StrategyInfoEditBox
                initialExpanded={true}
                strategyName={viewStore.strategyName}
                description={viewStore.description}
                onStrategyNameChange={(name) => viewStore.setStrategyName(name)}
                onDescriptionChange={(desc) => viewStore.setDescription(desc)}
            />

            {/* 저장 버튼 */}
            <Box>
                <button
                    className="btn btn-primary btn-lg btn-block"
                    disabled={!viewStore.canSubmit || viewStore.isSubmitting}
                    onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    {viewStore.isSubmitting ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            저장 중...
                        </>
                    ) : (
                        <>
                            <SaveIcon className="size-5" />
                            변경사항 저장
                        </>
                    )}
                </button>

                {/* 비활성화 이유 표시 */}
                {viewStore.disabledReason && (
                    <div className="text-error mt-2 text-center text-sm">
                        {viewStore.disabledReason}
                    </div>
                )}
            </Box>

            <Frame.BottomSpacer />
        </Frame>
    );
});

export default StrategyEditPage;
